"use client";

import { useServices } from "../services-context";
import { useMemo } from "react";
import { calculateDistance } from "@/app/[tenant]/self-schedule/utils";
import { cx } from "@/lib/tailwind-utils";
import { PublicServiceDetail } from "@/types/service";
import { EmptyServicesState } from "./empty-services-state";
import { GroupedServices, RenderBucket, ServiceGroup } from "./grouped-services";
import { computeAllowedLocationIds, filterServices, groupServicesByCategory, sortCategoryGroupsWithDisplayOrder, sortPromotedServicesFirst, sortServicesForSearch } from "./helpers";
import { PROMOTION_CATEGORY_NAME, hasRealPromotionCategory, isPromotionService } from "./promotion-category";
import { createSelectionHandler } from "./selection";
import { ServicesGrid } from "./services-grid";
import { useServicesView } from "./use-services-view";
import { FIRST_AVAILABLE_PROVIDER_ID } from "@/stores/self-schedule-store";
export const GroupedServicesView = ({
  className,
  onOpenDetails,
  onRequireMfa,
  onPhoneOnlyService,
  onOpenSelectionsDrawer
}: {
  className?: string;
  onOpenDetails?: (serviceId: string) => void;
  onRequireMfa?: (serviceId: string, onSelect?: (serviceId: string) => void) => void;
  onPhoneOnlyService?: (serviceId: string) => void;
  onOpenSelectionsDrawer?: (serviceId: string) => void;
}) => {
  const {
    state: {
      searchQuery: serviceSearchQuery,
      locationType,
      selectedLocation,
      patientType,
      selectedProviderId,
      data
    },
    actions: {
      setSelectedLocationId,
      addService: addSelectedService,
      removeAllServiceInstances
    }
  } = useServices();
  const {
    frontPageData,
    categories: _categories,
    isLoading,
    selectedCategory,
    isServiceSelected,
    getServiceInstanceCount,
    isServiceUnavailable,
    selectedServiceIds,
    canSelectMore,
    userLocation,
    handleQuantityChange,
    handleInfoClick,
    effectiveNumAllowedServices
  } = useServicesView({
    onOpenDetails,
    onRequireMfa,
    onPhoneOnlyService,
    onOpenSelectionsDrawer
  });
  const validLocations = useMemo(() => {
    if (!frontPageData?.locations) return [];
    return frontPageData.locations.map(location => {
      const distance = userLocation && location.latitude && location.longitude ? calculateDistance(userLocation.lat, userLocation.lng, location.latitude, location.longitude) : Infinity;
      return {
        location,
        distance
      };
    }).sort((a, b) => a.distance - b.distance);
  }, [frontPageData?.locations, userLocation]);
  const groupByLocation = useMemo(() => frontPageData?.shouldGroupServicesByLocation && userLocation, [frontPageData?.shouldGroupServicesByLocation, userLocation]);
  const providerFilterId = selectedProviderId === FIRST_AVAILABLE_PROVIDER_ID ? null : selectedProviderId;
  const groupedServicesBase: ServiceGroup[] = useMemo(() => {
    const groups = new Map<string, ServiceGroup>();
    const filtered = sortServicesForSearch(filterServices(frontPageData?.appointments, {
      searchQuery: serviceSearchQuery,
      selectedCategory,
      locations: frontPageData?.locations,
      locationType,
      selectedLocationId: selectedLocation?.id ?? null,
      selectedProviderId: providerFilterId,
      patientType,
      categories: data?.categories
    }), serviceSearchQuery);
    if (groupByLocation) {
      // Initialize location groups in distance order
      validLocations.forEach(({
        location: location_0,
        distance: distance_0
      }) => {
        const title = !location_0.isVirtual ? `Available At ${location_0.name}` : `Available via Telehealth`;
        groups.set(location_0.id, {
          id: location_0.id,
          title,
          distance: distance_0,
          location: location_0,
          services: []
        });
      });

      // Assign each service to the closest matching location only.
      // A service with no explicit locationIds is "available at all
      // locations" (matches the feed's per-location emission), so drop it
      // into the closest group — but only among groups that already pass the
      // active locationType filter, otherwise a Telehealth-only patient
      // could get an unassigned service bucketed into the nearest physical
      // clinic and end up requesting in-person availability.
      const allowedLocations = computeAllowedLocationIds(frontPageData?.locations, locationType);
      // A virtual service is by video wherever it's booked, so it sits in one
      // telehealth group with no location: picking it sets none.
      const virtualServices = filtered.filter(service => service.isVirtual);
      filtered.forEach(service_0 => {
        if (service_0.isVirtual) return;
        const noExplicitLocations = !service_0.locationIds?.length;
        for (const {
          location: location_1
        } of validLocations) {
          if (allowedLocations && !allowedLocations.has(location_1.id)) continue;
          if (noExplicitLocations || service_0.locationIds?.includes(location_1.id)) {
            const g = groups.get(location_1.id);
            if (g && g.services) g.services.push(service_0);
            break;
          }
        }
      });
      const locationGroups = Array.from(groups.values()).filter(g_0 => (g_0.services?.length ?? 0) > 0);
      return virtualServices.length > 0 ? [...locationGroups, {
        id: "virtual-services",
        title: "Available via Telehealth",
        services: virtualServices
      } as ServiceGroup] : locationGroups;
    }

    // Not grouping by location: single flat group
    return [{
      id: "all",
      title: "Available Services",
      services: filtered
    } as ServiceGroup];
  }, [frontPageData?.appointments, validLocations, selectedCategory, serviceSearchQuery, patientType, providerFilterId, data?.categories, locationType, selectedLocation, frontPageData?.locations, groupByLocation]);
  const categoryGrouping = useMemo(() => {
    const backendFlag = frontPageData?.shouldGroupServicesByCategory;
    const hasCategories = (frontPageData?.categories?.length ?? 0) > 0;
    return backendFlag && hasCategories;
  }, [frontPageData?.shouldGroupServicesByCategory, frontPageData?.categories]);
  const isSearching = serviceSearchQuery.trim().length > 0;
  const searchServices = useMemo(() => {
    if (!isSearching) return [];
    const eligibleIds = new Set(groupedServicesBase.flatMap(group => (group.services ?? []).map(service_1 => service_1.id)));
    // Keep the original location eligibility and configured relevance ties,
    // without carrying location groups into the search presentation.
    const services = (frontPageData?.appointments ?? []).filter(service_2 => eligibleIds.has(service_2.id));
    return sortServicesForSearch(services, serviceSearchQuery);
  }, [isSearching, groupedServicesBase, frontPageData?.appointments, serviceSearchQuery]);
  const groupsForRender: ServiceGroup[] = useMemo(() => {
    if (isSearching) return [];
    if (!categoryGrouping) return groupedServicesBase;
    if (groupByLocation) {
      // location groups with category buckets inside
      return groupedServicesBase.map(g_1 => {
        const useSyntheticPromotionGroup = !hasRealPromotionCategory(data?.categories);
        const promotionServices = useSyntheticPromotionGroup ? (g_1.services || []).filter(isPromotionService) : [];
        const bucketMap = new Map<string, PublicServiceDetail[]>();
        for (const s of g_1.services || []) {
          if (useSyntheticPromotionGroup && isPromotionService(s)) continue;
          const key = s.category ?? "Other Services";
          if (!bucketMap.has(key)) bucketMap.set(key, []);
          bucketMap.get(key)!.push(s);
        }
        const simpleGroups = Array.from(bucketMap.entries()).map(([name, services_0]) => ({
          id: name,
          title: name,
          services: sortPromotedServicesFirst(services_0)
        })).filter(b_0 => b_0.services.length > 0);
        // Depth metadata so the renderer can indent subcategory buckets.
        const categoryByName = new Map((frontPageData?.categories ?? []).map(c => [c.name, c]));
        const depthFor = (name_0: string): number => {
          let d = 0;
          let current: string | null | undefined = name_0;
          const seen = new Set<string>();
          while (current && !seen.has(current)) {
            seen.add(current);
            const node = categoryByName.get(current);
            if (!node?.parentCategoryName) break;
            current = node.parentCategoryName;
            d += 1;
          }
          return d;
        };
        const buckets: RenderBucket[] = sortCategoryGroupsWithDisplayOrder(simpleGroups, frontPageData?.categories).map(s_0 => ({
          id: s_0.id,
          name: s_0.title,
          services: s_0.services,
          depth: depthFor(s_0.id),
          parentCategoryName: categoryByName.get(s_0.id)?.parentCategoryName ?? null
        }));
        if (promotionServices.length > 0) {
          buckets.unshift({
            id: PROMOTION_CATEGORY_NAME,
            name: PROMOTION_CATEGORY_NAME,
            services: sortPromotedServicesFirst(promotionServices),
            depth: 0,
            parentCategoryName: null
          });
        }
        return {
          ...g_1,
          buckets
        } as ServiceGroup;
      });
    }

    // category as top-level groups (no buckets)
    const base = groupedServicesBase[0];
    return groupServicesByCategory(base?.services || [], frontPageData?.categories);
  }, [isSearching, categoryGrouping, groupByLocation, groupedServicesBase, frontPageData?.categories, data?.categories]);

  // Custom selection handler that also sets the location
  const handleServiceClick = useMemo(() => {
    return createSelectionHandler(frontPageData?.appointments, {
      addSelectedService,
      removeAllSelectedServiceInstances: removeAllServiceInstances,
      selectedServiceIds,
      numAllowedServices: effectiveNumAllowedServices,
      onRequireMfa,
      onPhoneOnlyService,
      locationType,
      onOpenSelectionsDrawer,
      onSelected: (serviceId: string) => {
        if (!groupByLocation) return;
        for (const group_0 of groupedServicesBase) {
          if ((group_0.services || []).some(service_3 => service_3.id === serviceId)) {
            if (group_0.location?.id) setSelectedLocationId(group_0.location.id, group_0.location.name);
            break;
          }
        }
      }
    });
  }, [frontPageData?.appointments, addSelectedService, removeAllServiceInstances, selectedServiceIds, effectiveNumAllowedServices, onRequireMfa, onPhoneOnlyService, locationType, onOpenSelectionsDrawer, groupByLocation, groupedServicesBase, setSelectedLocationId]);
  if (!frontPageData || isLoading) return null;
  if (isSearching) {
    return <div className={cx(className)}>
        {searchServices.length === 0 ? <EmptyServicesState /> : <ServicesGrid services={searchServices} selectedCategory={selectedCategory ?? ""} isServiceSelected={isServiceSelected} getServiceInstanceCount={getServiceInstanceCount} isServiceUnavailable={isServiceUnavailable} canSelectMore={canSelectMore} selectedCount={selectedServiceIds.length} maxQuantity={effectiveNumAllowedServices} onServiceClick={handleServiceClick} onQuantityChange={handleQuantityChange} onInfoClick={handleInfoClick} />}
      </div>;
  }
  return <div className={cx(className)}>
      <GroupedServices groups={groupsForRender} userLocation={userLocation} isServiceSelected={isServiceSelected} getServiceInstanceCount={getServiceInstanceCount} isServiceUnavailable={isServiceUnavailable} canSelectMore={canSelectMore} selectedCount={selectedServiceIds.length} maxQuantity={effectiveNumAllowedServices} onServiceClick={handleServiceClick} onQuantityChange={handleQuantityChange} onInfoClick={handleInfoClick} subgroupByCategory={!!categoryGrouping && !!groupByLocation} />
    </div>;
};
export default GroupedServicesView;